"use client";

import { Accordion } from "@base-ui/react/accordion";
import type { ReactNode } from "react";

type AccordionSectionProps = {
  /** ヘッダーに表示するタイトル */
  title: string;
  /** タイトル右側、chevron とは別に表示する要素 (例: アクセスの地図アイコン) */
  trailing?: ReactNode;
  /** 初期状態で開いておくか。デフォルト false */
  defaultOpen?: boolean;
  /** Panel 内に表示するコンテンツ */
  children: ReactNode;
};

/**
 * 共通アコーディオンセクション (base-ui ベース)。
 *
 * - ヘッダー: タイトル (trigger) + 任意の補助要素 + 展開 chevron (trigger)
 * - 開閉に応じて chevron が回転
 * - 複数の AccordionSection は独立して動作する (それぞれ別の Root を持つ)
 *
 * 設計上の注意:
 * - SANU のセクションヘッダーデザイン (bg-dx-surface-base, 20px semibold) を採用
 * - 補助要素 (例: 地図リンク) は `trailing` に渡す。クリックは accordion の trigger と独立
 */
export function AccordionSection({
  title,
  trailing,
  defaultOpen = false,
  children,
}: AccordionSectionProps) {
  return (
    <Accordion.Root defaultValue={defaultOpen ? ["section"] : []}>
      <Accordion.Item value="section">
        <Accordion.Header className="bg-dx-surface-base flex items-center py-[6px] pr-[8px] pl-[16px]">
          <Accordion.Trigger className="focus-visible:ring-dx-text-default flex flex-1 cursor-pointer items-start py-[10px] text-left outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2">
            <span className="font-notojp text-dx-base-black flex-1 text-[20px] leading-[1.4] font-semibold">
              {title}
            </span>
          </Accordion.Trigger>
          {trailing && (
            <div className="flex shrink-0 items-center">{trailing}</div>
          )}
          <Accordion.Trigger
            className="text-dx-text-default focus-visible:ring-dx-text-default flex size-[48px] shrink-0 cursor-pointer items-center justify-center rounded-full transition-transform duration-200 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 data-[panel-open]:rotate-180"
            aria-label="展開"
          >
            <ChevronDownIcon />
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Panel className="h-[var(--accordion-panel-height)] overflow-hidden transition-[height] duration-200 data-[ending-style]:h-0 data-[starting-style]:h-0">
          {children}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
}

/**
 * ロード中の placeholder。実体の header と同じ余白で高さを揃える。
 * Panel は閉じ前提なので header 行のみ。
 */
export function AccordionSectionSkeleton() {
  return (
    <div className="bg-dx-surface-base flex items-center py-[6px] pr-[8px] pl-[16px]">
      <div className="flex flex-1 items-start py-[10px]">
        <div className="bg-dx-surface-subtle h-[24px] w-[40%] animate-pulse rounded-sm" />
      </div>
      <div className="flex size-[48px] shrink-0 items-center justify-center">
        <div className="bg-dx-surface-subtle size-[20px] shrink-0 animate-pulse rounded-sm" />
      </div>
    </div>
  );
}

/**
 * `public/i/dx/expand-more.svg` を mask-image で着色するアイコン。
 * `bg-current` + `text-*` で色テーマを継承する。
 */
function ChevronDownIcon() {
  return (
    <span
      aria-hidden
      className="block size-[20px] shrink-0 bg-current"
      style={{
        WebkitMaskImage: "url(/i/icon_chevron_down.svg)",
        maskImage: "url(/i/icon_chevron_down.svg)",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}
